import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Asl } from 'src/app/model/Models';
import { RelFlussoAmbitoDto } from 'src/app/model/RelFlussoAmbito';
import { ApiService } from 'src/app/services/api.service';
import { DocumentService } from 'src/app/services/document.service';

@Component({
  selector: 'app-genera-invio-modal',
  templateUrl: './genera-invio-modal.component.html',
  styleUrls: ['./genera-invio-modal.component.css']
})
export class GeneraInvioModalComponent implements OnInit {

  @Input() aslList: Asl[] = [];

  // Variabili di navigazione e caricamento
  step: number = 1;
  loading: boolean = false;
  loadingInvio: boolean = false;

  // Selezioni attive
  annoSel: number | null = null;
  aslSel: Asl | null = null;
  flussoSel: { id: string, nome: string } | null = null;
  siglaSel: string | null = null;
  periodoSel: any | null = null;

  // Liste di scelta
  anni: number[] = [];
  flussiDisponibili: { id: string, nome: string }[] = [];
  sigleDisponibili: string[] = [];
  scadenzeDisponibili: any[] = [];

  // Variabili per il riepilogo (Step 6)
  isIcd10FlowSelected: boolean = false;
  anagrafiche: number = 0;
  contatti: number = 0;
  prestazioni: number = 0;
  stringPre: string = 'prestazioni/presenze';
  dataScadenzaFlusso: Date | null = null;
  idIstanzaFlussoInbound: number = 0;
  standardFlowHistoryExists: boolean = false;
  missingScadenzeMessage: string = '';

  resultInvii: any[] = [];
  canInviaDati: boolean = false;
  dataRifDa: string = '';
  dataRifA: string = '';

  constructor(
    public activeModal: NgbActiveModal,
    private apiService: ApiService,
    private documentService: DocumentService
  ) { }

  ngOnInit(): void {
    this.initAnni();
    this.caricaAmbiti();
  }

  private initAnni(): void {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= 1900; i--) {
      this.anni.push(i);
    }
  }

  // Step 3: Caricamento Ambiti (Codici Flusso)
  public caricaAmbiti(): void {
    this.loading = true;
    this.apiService.getAllRelFlussoAmbito().subscribe({
      next: (data: RelFlussoAmbitoDto[]) => {
        this.flussiDisponibili = data
          .filter(item => item.attiva)
          .map(item => ({
            id: item.codiceFlusso,
            nome: item.ambito
          }))
          .sort((a, b) => a.nome.localeCompare(b.nome));
          
        this.loading = false;
      },
      error: (err) => {
        console.error("Errore nel caricamento degli ambiti: ", err);
        this.loading = false;
      }
    });
  }

  // Getter che filtra la lista dinamicamente in base all'anno
  get flussiMostrati() {
    if (this.annoSel && this.annoSel < 2026) {
      return this.flussiDisponibili.filter(f => !f.id.toUpperCase().includes('ICD10'));
    }
    return this.flussiDisponibili;
  }

  // Step 4: Caricamento Sigle via Inbound
  public caricaSigle(): void {
    if (!this.flussoSel) return;
    this.loading = true;
    this.missingScadenzeMessage = '';
    this.apiService.getInboundSigle(this.flussoSel.id).subscribe({
      next: (sigle: string[]) => {
        this.sigleDisponibili = (sigle || []).sort((a, b) => a.localeCompare(b));
        this.loading = false;
        if(this.sigleDisponibili.length === 0) {
           this.missingScadenzeMessage = "Nessuna sigla disponibile per questo ambito.";
        }
      },
      error: (err) => {
        console.error("Errore nel caricamento sigle da inbound: ", err);
        this.loading = false;
        
        this.sigleDisponibili = []; 
        let errorMsg = "Errore durante il caricamento delle sigle.";
        if (err.error && err.error.message) {
            errorMsg = err.error.message;
        }
        this.missingScadenzeMessage = errorMsg;
      }
    });
  }

  // Step 5: Caricamento Scadenze via Inbound
  public caricaScadenze(): void {
    if (!this.flussoSel || !this.siglaSel || !this.annoSel || !this.aslSel) return;
    this.loading = true;
    this.missingScadenzeMessage = '';
    
    this.apiService.getInboundScadenze(
        this.flussoSel.id, 
        this.siglaSel, 
        this.annoSel.toString(), 
        this.aslSel.codiceAzienda!
    ).subscribe({
      next: (res: any) => {
        this.scadenzeDisponibili = (res.scadenze || []).sort((a: any, b: any) => (a.periodo || '').localeCompare(b.periodo || ''));
        this.loading = false;
        if(this.scadenzeDisponibili.length === 0) {
           this.missingScadenzeMessage = "Scadenza del flusso selezionato assente.";
        }
      },
      error: (err) => {
        console.error("Errore nel caricamento scadenze da inbound: ", err);
        this.loading = false;
        
        this.scadenzeDisponibili = [];
        let errorMsg = "Errore durante il caricamento delle scadenze.";
        if (err.error && err.error.message) {
            errorMsg = err.error.message;
        }
        this.missingScadenzeMessage = errorMsg;
      }
    });
  }

  // --- METODI DI NAVIGAZIONE E SELEZIONE ---
  chooseAnno(aa: number): void {
    this.annoSel = aa;
    this.step = 2;
  }

  chooseAsl(asl: Asl): void {
    this.aslSel = asl;
    this.step = 3;
  }

  chooseFlusso(f: { id: string, nome: string }): void {
    this.flussoSel = f;
    this.isIcd10FlowSelected = f.id.includes('ICD10');
    this.stringPre = f.id.includes('SEMIRES') ? 'presenze' : 'prestazioni';
    this.step = 4;
    this.caricaSigle();
  }

  chooseSigla(sigla: string): void {
    this.siglaSel = sigla;
    this.step = 5;
    this.caricaScadenze();
  }

  choosePeriodo(scadenza: any): void {
    this.periodoSel = scadenza;
    this.idIstanzaFlussoInbound = scadenza.id_scadenza;
    this.dataScadenzaFlusso = new Date(scadenza.data_scadenza_ministeriale);
    this.step = 6;
    this.dataRifDa = scadenza.data_rif_da;
    this.dataRifA = scadenza.data_rif_a;
    this.recuperaDatiRiepilogo(this.dataRifDa, this.dataRifA);
  }

  cancellaAnno(): void {
    this.annoSel = null;
    this.step = 1;
    this.cancellaASL();
    this.missingScadenzeMessage = ''; 
  }

  cancellaASL(): void {
    this.aslSel = null;
    if (this.step > 2) this.step = 2;
    this.cancellaFlusso();
    this.missingScadenzeMessage = '';
  }

  cancellaFlusso(): void {
    this.flussoSel = null;
    if (this.step > 3) this.step = 3;
    this.cancellaSigla();
    this.missingScadenzeMessage = '';
  }

  cancellaSigla(): void {
    this.siglaSel = null;
    if (this.step > 4) this.step = 4;
    this.cancellaPeriodo();
    this.missingScadenzeMessage = '';
  }

  cancellaPeriodo(): void {
    this.periodoSel = null;
    this.idIstanzaFlussoInbound = 0;
    if (this.step > 5) this.step = 5;
    this.missingScadenzeMessage = '';
  }

  // --- RECUPERO DATI E INVIO (STEP 6) ---
  recuperaDatiRiepilogo(dataRifDa: string, dataRifA: string): void {
    if (!this.annoSel || !this.aslSel || !this.flussoSel || !this.periodoSel) return;

    this.loading = true;
    this.canInviaDati = false;
    this.missingScadenzeMessage = '';
    this.anagrafiche = 0;
    this.contatti = 0;
    this.prestazioni = 0;
    
    const annoStr = this.annoSel.toString();
    const aslCodice = this.aslSel.codiceAzienda!;
    const flussoId = this.flussoSel.id;
    const etichetta = this.periodoSel.etichetta.toString();

    // Controllo per anni precedenti al 2026 sui flussi ICD10
    if (this.isIcd10FlowSelected && this.annoSel < 2026) {
      this.missingScadenzeMessage = "Per anni precedenti al 2026 il flusso ICD10 non e previsto.";
      this.loading = false;
      return;
    }

    if (this.isIcd10FlowSelected) {
      this.documentService.checkStandardPositiveTransmission(annoStr, flussoId, etichetta, "", aslCodice).subscribe((exists: any) => {
        this.standardFlowHistoryExists = exists;
        this.recuperaConteggi(annoStr, flussoId, dataRifDa, dataRifA, aslCodice);
      });
    } else {
      this.recuperaConteggi(annoStr, flussoId, dataRifDa, dataRifA, aslCodice);
    }
  }

  private recuperaConteggi(anno: string, flusso: string, dataRifDa: string, dataRifA: string, asl: string): void {
    if (this.isIcd10FlowSelected) {
      this.documentService.getContattiIcd10(anno, flusso, dataRifDa, dataRifA, "", asl).subscribe((res: any) => {
        this.contatti = res || 0;
        
        this.canInviaDati = this.standardFlowHistoryExists && this.contatti > 0; 
        this.loading = false;
      });
    } else {
      this.documentService.getAnagrafiche(anno, flusso, dataRifDa, dataRifA, "", asl).subscribe((ana: any) => {
        this.anagrafiche = ana || 0;
        this.documentService.getContatti(anno, flusso, dataRifDa, dataRifA, "", asl).subscribe((con: any) => {
          this.contatti = con || 0;
          this.documentService.getPrestazioni(anno, flusso, dataRifDa, dataRifA, "", asl).subscribe((pre: any) => {
            this.prestazioni = pre || 0;
            
            this.canInviaDati = (this.anagrafiche > 0 || this.contatti > 0 || this.prestazioni > 0);
            this.loading = false;
          });
        });
      });
    }
  }

  inviaRegione(dataRifDa: string, dataRifA: string): void {
    if (!this.annoSel || !this.flussoSel || !this.periodoSel || !this.aslSel || !this.siglaSel) return;
    
    this.loadingInvio = true;

    const requestPayload = {
      anno: this.annoSel.toString(),
      flusso: this.flussoSel.id,
      etichetta: this.periodoSel.etichetta.toString(),
      dataRifDa: dataRifDa,
      dataRifA: dataRifA,
      struttura: "",
      asl: this.aslSel.codiceAzienda!,
      codiceFlusso: this.flussoSel.id,
      siglaFlusso: this.siglaSel,
      idScadenzaInbound: this.idIstanzaFlussoInbound,
      idFlussoScadenza: this.periodoSel.id_flusso ? this.periodoSel.id_flusso : undefined,
      nomeAmbito: this.flussoSel.nome
    };

    this.documentService.inviaDatiRegione(requestPayload).subscribe({
      next: (res: any) => {
        this.loadingInvio = false;
        
        const risultati = res && res.invii ? res.invii : [res];
        const hasErrors = risultati.some((r: any) => !r.esito && !r.successo);

        if (!hasErrors) {
          this.activeModal.close({ status: 'SUCCESS', message: 'Trasmissione avvenuta con successo!' });
        } else {
          this.resultInvii = risultati;
        }
      },
      error: (err) => {
        console.error("Errore invio a regione:", err);
        this.loadingInvio = false;
        let errorMsg = "Errore durante la comunicazione con il server.";
        if (err.error && err.error.message) {
             errorMsg = err.error.message;
        } else if (err.error && typeof err.error === 'string') {
             try {
                 const parsed = JSON.parse(err.error);
                 if (parsed.message) errorMsg = parsed.message;
                 else if (parsed.error) errorMsg = parsed.error;
             } catch(e) {
                 errorMsg = err.error;
             }
        }
        this.resultInvii = [{ successo: false, idInstanzaInvio: 0, message: errorMsg }];
      }
    });
  }

  chiudi(aggiorna: boolean = false): void {
    if (aggiorna) {
      this.activeModal.close('INVIO_EFFETTUATO');
    } else {
      this.activeModal.dismiss('Annullato');
    }
  }
}